import React, {useState, useEffect} from "react";
import Container from "react-bootstrap/Container";
import {Alert, Breadcrumb, Col, Nav, Row, Table} from "react-bootstrap";
import {Link} from "react-router-dom";

const sections = [
    {id: 'general',    label: 'General'},
    {id: 'performers', label: 'For Performers'},
    {id: 'hosts',      label: 'For Hosts'},
    {id: 'matching',   label: 'Matching'},
    {id: 'volunteers', label: 'Volunteers'},
    {id: 'committee',  label: 'The Committee'},
    {id: 'site-help',  label: 'Site Help'},
];

const FAQ = () => {
    const [activeSection, setActiveSection] = useState('general');

    useEffect(() => {
        const handleScroll = () => {
            let current = sections[0].id;
            sections.forEach(({id}) => {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= 120) {
                    current = id;
                }
            });
            setActiveSection(current);
        };
        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (e, id) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    return (
        <>
            <Container fluid>
                <Row>
                    <Col>
                        <h3>
                            <Breadcrumb>
                                <Breadcrumb.Item active>FAQ</Breadcrumb.Item>
                            </Breadcrumb>
                        </h3>
                    </Col>
                </Row>
                <Row>
                    {/* ── Sticky sidebar nav (md and up) ── */}
                    <Col md={3} className="d-none d-md-block">
                        <div style={{position: 'sticky', top: '1rem'}}>
                            <Nav className="flex-column" variant="pills">
                                {sections.map(({id, label}) => (
                                    <Nav.Link
                                        key={id}
                                        href={`#${id}`}
                                        active={activeSection === id}
                                        onClick={e => scrollTo(e, id)}
                                    >
                                        {label}
                                    </Nav.Link>
                                ))}
                            </Nav>
                        </div>
                    </Col>

                    {/* ── FAQ content ── */}
                    <Col md={9}>

                        <Alert variant="primary" className="mb-4">
                            <strong>Want to help make Porchfest a success?</strong>{' '}
                            We're always looking for enthusiastic people to lend their ideas and energy.{' '}
                            <Alert.Link href="#volunteers" onClick={e => scrollTo(e, 'volunteers')}>
                                Learn how to get involved →
                            </Alert.Link>
                        </Alert>

                        {/* ── General ── */}
                        <h4 id="general">General</h4>
                        <hr/>

                        <h5><strong>Do I have to register to use this site?</strong></h5>
                        <p>
                            No, all of the information on the site is available to everyone, whether you have
                            registered or not. If you just want to attend Porchfest, you don't have to register.
                        </p>
                        <br/>

                        <h5><strong>Why should I register?</strong></h5>
                        <p>
                            You should register if you are planning to participate in Porchfest as a performer or a host.
                        </p>
                        <p>
                            If you register as a <strong>performer</strong>, you can add information about your act and
                            search for venues that fit your preferred time slot. If you find a venue you like, you can
                            contact the owner using the "Contact" button on their venue page and work out the details.
                        </p>
                        <p>
                            If you register as a <strong>host</strong>, you can add information about your venue and
                            your time slot availability, and search for acts you might be interested in hosting. If you
                            find a good fit, you can contact the owner using the "Contact" button on their act page.
                        </p>
                        <br/>

                        <h5><strong>Is there a map of performances?</strong></h5>
                        <p>
                            Yes! The final Porchfest schedule, including a map of all venues and performances, is
                            published on our site at the end of May. Keep an eye on the site as the event approaches.
                        </p>
                        <br/>

                        <h5><strong>What happens if it rains?</strong></h5>
                        <p>
                            The Porchfest committee will announce rain date plans in the event of inclement weather.
                            Keep an eye on our site and social media for updates.
                        </p>
                        <br/>

                        <h5><strong>I have photos and videos from Porchfest on my social media. What should I tag
                            them with?</strong></h5>
                        <p>
                            We'd love it if you could tag your social media posts with <span
                            className="badge text-bg-secondary font-monospace">#winporchfest2026</span>.
                        </p>
                        <br/>

                        {/* ── For Performers ── */}
                        <h4 id="performers">For Performers</h4>
                        <hr/>

                        <h5><strong>How do I register as a performer?</strong></h5>
                        <p>
                            Go to our <Link to="/register" reloadDocument="true">registration</Link> page and select
                            the "register as a Performer" option. This will allow you to login to our site and manage
                            your act.
                        </p>
                        <br/>

                        <h5><strong>I am a self-hosted performer — how should I register?</strong></h5>
                        <p>
                            Select both "register as a Host" and "register as a Performer" on
                            our <Link to="/register" reloadDocument="true">registration</Link> page. You can then
                            create your venue and act, and schedule the act into one of your available slots, all with
                            a single account.
                        </p>
                        <br/>

                        <h5><strong>I am a performer looking for a host. What should I do?</strong></h5>
                        <p>
                            Search for hosts on our <Link to="/venues" reloadDocument="true">venues</Link> page,
                            using filters to narrow down venues by preferred genre and available time slots.
                        </p>
                        <p>
                            Once you find a venue you like, click the "Contact" button on their venue page and fill
                            out the contact form to let the owner know you're interested. The venue owner will be
                            notified and you'll be able to continue the conversation via email.
                        </p>
                        <br/>

                        <h5><strong>Can I perform for longer or shorter than one hour?</strong></h5>
                        <p>
                            Slots are one hour long, but some flexibility is possible. If you'd like to perform
                            for longer — say, 90 minutes — use the "Contact" button on your host's venue page to
                            ask them to block the following slot for you. Make sure to do this early so your host
                            can plan ahead and avoid scheduling another act in that slot. If you'd like to perform
                            for less than an hour, just let your host know in advance so they can plan the
                            transition between acts accordingly.
                        </p>
                        <br/>

                        <h5><strong>What am I expected to do as a performer?</strong></h5>
                        <p>
                            By registering on our site, a performer agrees to:
                        </p>
                        <ul>
                            <li>
                                <strong>Provide a performance</strong> consisting of a musical act, dance, or poetry.
                                The performer will provide a description of the performance on our site.
                            </li>
                            <li>
                                <strong>Stick to the program on Porchfest day.</strong> You are expected to perform
                                within the time slot you agreed upon with the host, and be timely with respect to
                                arrival time, setup, etc.
                            </li>
                            <li>
                                <strong>Bring your own equipment</strong> to the venue. If you need electric power or
                                other accommodations, please mention that to potential venues when you contact them.
                            </li>
                            <li>
                                <strong>Be respectful</strong> of the host, venue, and audience. Please work with your
                                venue owner to ensure a smooth performance from setup to final clean up.
                            </li>
                        </ul>
                        <br/>

                        {/* ── For Hosts ── */}
                        <h4 id="hosts">For Hosts</h4>
                        <hr/>

                        <h5><strong>How do I register as a host?</strong></h5>
                        <p>
                            Go to our <Link to="/register" reloadDocument="true">registration</Link> page and select
                            the "register as a Host" option. This will allow you to login to our site and manage your
                            venue.
                        </p>
                        <br/>

                        <h5><strong>I am a host looking for a performer. What should I do?</strong></h5>
                        <p>
                            Search for performers on our <Link to="/acts" reloadDocument="true">acts</Link> page,
                            using filters to narrow down acts by genre, preferred time slot, and status.
                        </p>
                        <p>
                            Once you find an act you like, click the "Contact" button on their act page and fill out
                            the contact form to let the owner know you're interested. The act owner will be notified
                            and you'll be able to continue the conversation via email.
                        </p>
                        <p>
                            After agreeing to match, <strong>it is the host's responsibility to schedule the
                            performer's act into the agreed-upon time slot</strong>. To do this:
                        </p>
                        <ol>
                            <li>Login to the site (if you have not done so already).</li>
                            <li>Select your venue from the <Link to="/myvenues" reloadDocument="true">my
                                venues</Link> page.
                            </li>
                            <li>Click the "Slots / Schedule" button next to the venue name.</li>
                            <li>Select the act you want to schedule for the agreed-upon slot. Note that if you don't
                                see your act, it means they are already scheduled for this slot at another venue.
                            </li>
                            <li>Click the Save button at the bottom of the page.</li>
                        </ol>
                        <br/>

                        <h5><strong>What time slots are available for hosting?</strong></h5>
                        <p>
                            We ask each host to provide their availability for one or more of the following time slots:
                        </p>
                        <Table bordered hover size="lg" responsive>
                            <thead className="table table-striped">
                            <tr>
                                <th>Slot</th>
                                <th>Start</th>
                                <th>End</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>1</td>
                                <td>12:00pm</td>
                                <td>1:00pm</td>
                            </tr>
                            <tr>
                                <td>2</td>
                                <td>1:00pm</td>
                                <td>2:00pm</td>
                            </tr>
                            <tr>
                                <td>3</td>
                                <td>2:00pm</td>
                                <td>3:00pm</td>
                            </tr>
                            <tr>
                                <td>4</td>
                                <td>3:00pm</td>
                                <td>4:00pm</td>
                            </tr>
                            <tr>
                                <td>5</td>
                                <td>4:00pm</td>
                                <td>5:00pm</td>
                            </tr>
                            </tbody>
                        </Table>
                        <p>
                            Performers should leave enough time at the end of their slot for any following performer to
                            do their setup. Coordination with the host regarding setup times is highly encouraged.
                        </p>
                        <p>
                            If you are a host and flexible on time but want to host only one act, you can initially
                            make all slots available. Once you agree with a performer on a slot, you can make the
                            others unavailable.
                        </p>
                        <p>
                            If you are a performer flexible on time, you can initially make yourself available for all
                            slots. Once a host schedules you for one, you may decide to remove the others from your
                            preferences.
                        </p>
                        <br/>

                        <h5><strong>What am I expected to do as a host?</strong></h5>
                        <p>
                            By registering on our site, a host agrees to:
                        </p>
                        <ul>
                            <li>
                                <strong>Provide a venue</strong> for one or more acts to perform during one of your
                                available time slots. The venue can be a porch or patio, a driveway or a parking lot,
                                with room for performers and their equipment, as well as an audience area that does not
                                block sidewalks or driveways.
                            </li>
                            <li>
                                <strong>Coordinate venue activities</strong> on Porchfest day. You will be the
                                performer's go-to person, making sure acts end on time and handling any issues that
                                come up.
                            </li>
                            <li>
                                <strong>Clean up</strong> after performances, using your own trash and recycling bins.
                            </li>
                            <li>
                                <strong>Keep neighbors in the loop.</strong> Please be a good neighbor and let them
                                know about your plans for Porchfest day to avoid any surprises.
                            </li>
                            <li>
                                <strong>Display a Porchfest lawn sign</strong> at your venue. We will provide signs
                                ahead of the event so people know you'll be hosting.
                            </li>
                        </ul>
                        <br/>

                        {/* ── Matching ── */}
                        <h4 id="matching">Matching</h4>
                        <hr/>

                        <h5><strong>How does a matched host and performer cancel their match?</strong></h5>
                        <p>
                            If a host and performer agree to cancel, it is the host's responsibility to un-schedule
                            the performer's act from the agreed-upon time slot. This puts the act back on the list of
                            performers looking for a match. Steps:
                        </p>
                        <ol>
                            <li>Login to the site (if you have not done so already).</li>
                            <li>Find your venue in the <Link to="/myvenues" reloadDocument="true">my
                                venues</Link> page.
                            </li>
                            <li>Click the "Slots / Schedule" button next to the venue name.</li>
                            <li>Unselect the act from the slot you previously agreed upon.</li>
                            <li>Click the Save button at the bottom of the page.</li>
                        </ol>
                        <p>
                            If you have any trouble cancelling a match, please <Link to="/contact"
                                                                                     reloadDocument="true">contact</Link> us
                            and we will help you.
                        </p>
                        <br/>

                        <h5><strong>I am a host or performer who cannot find a match. What can I do?</strong></h5>
                        <p>
                            The earlier you register and reach out to potential matches, the better your chances —
                            so don't wait! Once registration closes, the Porchfest committee reviews all remaining
                            unmatched acts and venues and does its best to help facilitate connections. While we can't
                            guarantee a match for everyone, we'll do our best to reach out and explore options. In the
                            meantime, please <Link to="/contact" reloadDocument="true">contact us</Link> if you'd
                            like help.
                        </p>
                        <br/>

                        <h5><strong>I am a performer interested in a specific host, but I don't see the "Contact" button next to the host's name.</strong></h5>
                        <p>
                            The "Contact" button next to the host name is only shown if you are logged in to the site.
                            Please make sure you are <Link to="/login" reloadDocument="true">logged in</Link>.
                        </p>
                        <br/>

                        {/* ── Volunteers ── */}
                        <h4 id="volunteers">Volunteers</h4>
                        <hr/>

                        <h5><strong>How can I volunteer?</strong></h5>
                        <p>
                            Porchfest is organized and run entirely by volunteers, and we'd love your help! Volunteers
                            contribute in a variety of ways — from helping coordinate logistics on the day of the
                            event, to spreading the word in the community, to lending fresh ideas for how to make
                            Porchfest even better. If you're interested in getting involved, please reach out to us
                            via our <Link to="/contact" reloadDocument="true">contact</Link> page. The more hands on
                            deck, the merrier!
                        </p>
                        <br/>

                        {/* ── The Committee ── */}
                        <h4 id="committee">The Committee</h4>
                        <hr/>

                        <h5><strong>What does the Porchfest committee do?</strong></h5>
                        <p>
                            The Porchfest committee will:
                        </p>
                        <ul>
                            <li>Oversee the registration process and help assess the viability of venues, if
                                necessary.
                            </li>
                            <li>Help with any issues related to matching.</li>
                            <li>Coordinate logistics for Porchfest day.</li>
                            <li>Provide lawn signs for venues.</li>
                            <li>Publish the final Porchfest schedule on our site.</li>
                            <li>Announce rain date plans, if necessary.</li>
                        </ul>
                        <br/>

                        <h5><strong>When is the final Porchfest schedule published?</strong></h5>
                        <p>
                            The final Porchfest schedule is published at the end of May.
                        </p>
                        <br/>

                        {/* ── Site Help ── */}
                        <h4 id="site-help">Site Help</h4>
                        <hr/>

                        <h5><strong>How do I login?</strong></h5>
                        <p>
                            After you register, you can use the email and password you created on
                            our <Link to="/login" reloadDocument="true">login</Link> page.
                        </p>
                        <br/>

                        <h5><strong>I forgot my password. What should I do?</strong></h5>
                        <p>
                            Use our <Link to="/forgotpass" reloadDocument="true">forgot password</Link> page to reset
                            your password.
                        </p>
                        <br/>

                        <h5><strong>I am having trouble using the site. What should I do?</strong></h5>
                        <p>
                            If you have any trouble with the site or have a general question, please use
                            our <Link to="/contact" reloadDocument="true">contact</Link> page to reach out to us.
                        </p>
                        <br/>

                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default FAQ;
